import React, { useEffect, useRef, useState, useContext } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalShipping as LocalShippingIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { getReq, delReq, putReq } from "../../configs/ApiUrl";
import { FurnitureSearch } from "../../App.js";
import DeleteWarningModal from "./util/DeleteWarningModal";
import AlwaysTop from "./util/AlwaysTop.js";
import defaultImg from "../../images/defaultImg.webp";
import DataTable from "../DataTable.js";

function LiveListings() {
  const { t } = useTranslation(["admin", "tooltips", "userProfile"]);
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const [searchTerm, setSearchTerm] = useContext(FurnitureSearch);
  const [loading, setLoading] = useState(false);
  const [historyProducts, setHistoryProducts] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [userRole, setUserRole] = useState("");
  const [subadminEmail, setSubadminEmail] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [courier, setCourier] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openSnackbar, setOpenSnackbar] = useState(false);

  const scrollToTop = () => {
    if (containerRef.current) {
      window.scrollTo({
        top: containerRef.current.offsetTop - 200,
        behavior: "smooth",
      });
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    setTimeout(scrollToTop, 150);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
    setTimeout(scrollToTop, 150);
  };

  const fetchUserProfile = async () => {
    const profile = await getReq("/user/profile");
    const userId = profile?.user?._id;
    const email = profile?.user?.email;
    setUserEmail(email);

    if (userId) {
      const roleResp = await getReq(`/user/${userId}`);
      if (roleResp?.success) {
        const { role, email: fetchedEmail, companyName } = roleResp;
        setUserRole(role);
        if (role === "subadmin") {
          setSubadminEmail(fetchedEmail);
          setCourier(companyName);
        }
      }
    }
  };

  const getAllHistoryProducts = async (role = userRole, email = userEmail) => {
    setLoading(true);
    const res = await getReq("/product/all");
    if (res?.allProducts) {
      const filtered = res.allProducts.filter((p) => {
        if (role === "admin")
          return ["approved", "preApproved"].includes(p.status);
        if (role === "subadmin")
          return (
            ["approved", "preApproved"].includes(p.status) &&
            p.subadminEmail === email
          );
        return false;
      });
      setHistoryProducts([...filtered]);
    } else {
      setHistoryProducts([]);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    setShowDeleteModal(false); // close modal first
    setLoadingStates((prev) => ({ ...prev, [id]: true }));
    await delReq(`/product/history/${id}`, { status: "declined" });
    await getAllHistoryProducts(userRole, userEmail);
    setLoadingStates((prev) => ({ ...prev, [id]: false }));
    setPage(0);
    message.success(t("admin:productDeleted", "Product deleted"));
  };

  const handleToggleVisibility = async (product) => {
    const { _id, isProductVisible } = product;
    setLoadingStates((prev) => ({ ...prev, [_id]: true }));
    await putReq("/product/visibility", {
      id: _id,
      isProductVisible: !isProductVisible,
    });
    setHistoryProducts((prev) =>
      prev.map((p) =>
        p._id === _id ? { ...p, isProductVisible: !isProductVisible } : p
      )
    );
    setLoadingStates((prev) => ({ ...prev, [_id]: false }));
  };

  const openDeleteModal = (id) => {
    setSelectedProductId(id);
    setShowDeleteModal(true);
  };

  const renderEditButton = (product) => {
    const match = historyProducts.filter((x) => x._id === product._id);
    setSearchTerm({ ...searchTerm, adminSearch: match });
    navigate(
      userRole === "admin" ? "/admin-product-view" : "/subadmin-edit-approval"
    );
  };

  const handleToggleDeliveryStatus = async (product) => {
    const { _id, status } = product;
    const newStatus = status === "approved" ? "preApproved" : "approved";
    setLoadingStates((prev) => ({ ...prev, [_id]: true }));

    try {
      if (userRole === "subadmin") {
        const update = await putReq(`/product/update-subadmin-email`, {
          id: _id,
          subadminEmail,
          courier,
        });
        if (!update?.success) throw new Error("Subadmin update failed");
      }

      const statusUpdate = await putReq(`/product/status`, {
        id: _id,
        status: newStatus,
      });
      if (!statusUpdate?.success) throw new Error("Status update failed");

      await getAllHistoryProducts(userRole, userEmail);
    } catch (err) {
      console.error("Failed to toggle delivery status", err);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [_id]: false }));
    }
    setPage(0);
  };

  const handleView = (product) => {
    const { _id, title } = product;
    setSearchTerm({ ...searchTerm, displayName: title, searchID: _id });
    window.open(`/catalogue-product/${_id}`, "_blank");
  };

  useEffect(() => {
    AlwaysTop();
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (userRole) getAllHistoryProducts(userRole, userEmail);
  }, [userRole, userEmail]);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= historyProducts.length) {
      setPage(0);
    }
  }, [historyProducts.length, page, rowsPerPage]);

  const columns = [
    {
      header: t("tables:product"),
      accessor: (p) => (
        <Typography
          variant='subtitle2'
          fontWeight={600}>
          {p.title}
        </Typography>
      ),
      mobile: true,
      bold: true,
    },
    {
      header: "Status",
      accessor: (p) =>
        p.status === "approved" || p.status === "preApproved" ? (
          <Chip
            label={
              p.status === "approved"
                ? t("userProfile:approvedSubtitle")
                : t("userProfile:preApprovedSubtitle")
            }
            color={p.status === "approved" ? "success" : "warning"}
          />
        ) : null,
      mobile: true,
    },
    {
      header: "Visibility",
      accessor: (p) => (
        <Chip
          label={
            p.isProductVisible
              ? t("admin:visibleLabel")
              : t("admin:hiddenLabel")
          }
          color={p.isProductVisible ? "success" : "default"}
        />
      ),
      mobile: true,
    },
  ];

  return (
    <Box
      ref={containerRef}
      className='flex flex-col items-center min-h-screen w-full px-2'>
      {showDeleteModal && (
        <DeleteWarningModal
          onConfirm={() => handleDelete(selectedProductId)}
          onClose={() => setShowDeleteModal(false)}
        />
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <DataTable
          columns={columns}
          data={historyProducts}
          getImage={(p) => p.mainImage || p.imageNames?.[0] || defaultImg}
          getRowKey={(p) => p._id}
          renderRowActions={(product) => (
            <>
              <Tooltip title={t("tooltips:view")}>
                <IconButton onClick={() => handleView(product)}>
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("tooltips:edit")}>
                <IconButton onClick={() => renderEditButton(product)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("tooltips:toggleVisibility")}>
                <IconButton onClick={() => handleToggleVisibility(product)}>
                  {product.isProductVisible ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title={t("tooltips:delete")}>
                <IconButton
                  onClick={() => openDeleteModal(product._id)}
                  color='error'>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
              {userRole === "admin" && (
                <Tooltip
                  title={
                    product.status === "approved"
                      ? t("admin:courierNeeded")
                      : t("admin:courierFuilfilled")
                  }>
                  <span>
                    <IconButton
                      onClick={() => handleToggleDeliveryStatus(product)}
                      disabled={!!loadingStates[product._id]}>
                      {loadingStates[product._id] ? (
                        <CircularProgress size={18} />
                      ) : (
                        <LocalShippingIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </>
          )}
          emptyMessage={t("admin:noProducts")}
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={handleChangePage}
          handleRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
}

export default LiveListings;
